import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import { useAuth } from "@/hooks/useAuth";
import type { MessageThread } from "@/types";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { EmptyState } from "@/components/ui/EmptyState";

const MESSAGE_THREADS_QUERY = gql`
  query MessageThreads {
    messageThreads {
      threadId
      otherUser {
        id
        displayName
        avatarUrl
      }
      listing {
        id
        displayTitle
        game {
          id
          title
          thumbnailUrl
        }
      }
      lastMessage {
        id
        body
        createdAt
        sender {
          id
        }
      }
      unreadCount
    }
  }
`;

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return "ką tik";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `prieš ${minutes} min.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `prieš ${hours} val.`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `prieš ${days} d.`;
  return date.toLocaleDateString("lt-LT");
}

function computeThreadId(userId1: string, userId2: string, listingId?: string): string {
  const ids = [userId1, userId2].sort();
  const base = `${ids[0]}-${ids[1]}`;
  return listingId ? `${base}-${listingId}` : base;
}

export function MessagesPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const listingId = searchParams.get("listing");
  const sellerId = searchParams.get("seller");

  // Redirect to thread when coming from "Message seller" button
  if (user && sellerId && listingId) {
    const threadId = computeThreadId(user.id, sellerId, listingId);
    return <Navigate to={`/messages/${threadId}?listing=${listingId}&seller=${sellerId}`} replace />;
  }

  const { data, isLoading } = useQuery({
    queryKey: ["messageThreads"],
    queryFn: () =>
      graphqlClient.request<{ messageThreads: MessageThread[] }>(
        MESSAGE_THREADS_QUERY
      ),
  });

  const threads = data?.messageThreads || [];

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
      <h1 className="mb-8 text-2xl font-bold text-gray-900">Žinutės</h1>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : threads.length === 0 ? (
        <EmptyState
          title="Žinučių nėra"
          description="Kol kas neturite jokių pokalbių."
          icon={
            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          }
        />
      ) : (
        <div className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
          {threads.map((thread) => (
            <Link
              key={thread.threadId}
              to={`/messages/${thread.threadId}`}
              className={`flex items-center gap-4 p-4 transition-colors hover:bg-gray-50 ${
                thread.unreadCount > 0 ? "bg-blue-50/50" : ""
              }`}
            >
              {/* Avatar with game icon */}
              <div className="relative shrink-0">
                {thread.otherUser.avatarUrl ? (
                  <img
                    src={thread.otherUser.avatarUrl}
                    alt={thread.otherUser.displayName}
                    className="h-12 w-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-800 text-sm font-semibold text-white">
                    {thread.otherUser.displayName[0].toUpperCase()}
                  </div>
                )}
                {thread.listing?.game?.thumbnailUrl && (
                  <img
                    src={thread.listing.game.thumbnailUrl}
                    alt={thread.listing.game.title}
                    className="absolute -bottom-1 -right-1 h-6 w-6 rounded border-2 border-white object-cover"
                  />
                )}
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className={`truncate text-sm ${thread.unreadCount > 0 ? "font-bold text-gray-900" : "font-medium text-gray-900"}`}>
                    {thread.otherUser.displayName}
                  </p>
                  <span className="shrink-0 text-xs text-gray-500">
                    {timeAgo(thread.lastMessage.createdAt)}
                  </span>
                </div>

                <p className={`mt-0.5 truncate text-sm ${thread.unreadCount > 0 ? "font-medium text-gray-700" : "text-gray-500"}`}>
                  {thread.lastMessage.body.length > 80
                    ? thread.lastMessage.body.slice(0, 80) + "..."
                    : thread.lastMessage.body}
                </p>

                {thread.listing && (
                  <p className="mt-1 truncate text-xs text-gray-400">
                    {thread.listing.displayTitle}
                  </p>
                )}
              </div>

              {/* Unread badge */}
              {thread.unreadCount > 0 && (
                <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary-800 px-1.5 text-[11px] font-bold text-white">
                  {thread.unreadCount}
                </span>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
